import React from 'react';

import { v4 as uuidv4 } from 'uuid';

import { Typography } from '@mui/material';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import {
  FlexCol,
  FlexRow,
  Spacer,
  TitleSmall,
} from '../../../common/components/v2';
import { Resource } from '../../../common/constants/resource';
import { TaskState } from '../../../common/constants/states';
import ElementLocators from '../../../common/ElementLocators';
import { getFormattedTimestamp } from '../../../common/helpers';
import { ITask } from '../../tasks/interfaces/ITask';
import { MIBCodeback } from '../../tasks/types/mibCodeback';
import styles from '../styles.module.scss';
import { NoraTaskIcon } from './components/NoraTaskIcon';
import { TaskDetailsItem } from './components/TaskDetailsItem';

export const MIBCodebackDisplay = ({ task }: { task: ITask }) => {
  const mibCodebackData: MIBCodeback = task.taskData as MIBCodeback;
  const codes = mibCodebackData && mibCodebackData.codes;
  const updateResult = Array.isArray(mibCodebackData?.updateResult)
    ? mibCodebackData.updateResult
    : null;
  const errorMsg = mibCodebackData?.updateResult
    ? (mibCodebackData.updateResult as any).message
    : null;
  const resultMap = new Map();
  updateResult?.forEach(row => {
    if (row?.length > 1) {
      resultMap.set(row[0], row[1]);
    }
  });

  return (
    <div>
      {errorMsg && (
        <TaskDetailsItem
          leftItem={Resource.ERROR_WITH_MESSAGE}
          rightItem={errorMsg}
        />
      )}
      <FlexRow>
        <FlexCol>
          {codes &&
            !updateResult &&
            codes.map(code => (
              <FlexRow
                key={uuidv4()}
                className={`${styles.Info} ${styles.MIBCode}`}
              >
                {code}
              </FlexRow>
            ))}
          {codes && updateResult && (
            <div>
              <Table sx={{ minWidth: '600px' }} aria-label="MIB Codebacks">
                <TableHead>
                  <TableRow>
                    <TableCell align="left" sx={{ width: '140px' }}>
                      <Typography variant="overline" color="textSecondary">
                        Code
                      </Typography>
                    </TableCell>
                    <TableCell align="left" sx={{ width: '160px' }}>
                      <Typography variant="overline" color="textSecondary">
                        Update Status
                      </Typography>
                    </TableCell>
                    <TableCell align="left">
                      <Typography variant="overline" color="textSecondary">
                        Message
                      </Typography>
                    </TableCell>
                    <TableCell align="left">
                      <Typography variant="overline" color="textSecondary">
                        Time
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {codes &&
                    updateResult &&
                    codes.map(code => (
                      <TableRow
                        key={code}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                        }}
                      >
                        <TableCell component="th" scope="row">
                          {code}
                        </TableCell>
                        <TableCell align="left">
                          <Alert
                            severity={
                              resultMap.get(code)?.isSuccess
                                ? 'success'
                                : 'warning'
                            }
                          >
                            {resultMap.get(code)?.isSuccess
                              ? 'Success'
                              : 'Failed'}
                          </Alert>
                        </TableCell>
                        <TableCell align="left">
                          {resultMap
                            .get(code)
                            ?.message?.replace(
                              /Form Instance Response Data - /g,
                              '',
                            ) || ''}
                        </TableCell>
                        <TableCell align="left">
                          {task.updatedAt
                            ? getFormattedTimestamp(task.updatedAt)
                            : ''}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          )}
        </FlexCol>
      </FlexRow>
    </div>
  );
};

export const MIBCodebackDetail = ({
  task,
  taskStateHandler,
}: {
  task: ITask;
  taskStateHandler: (taskId: number, state: TaskState, reason?: string) => void;
}) => {
  const onTaskStateChange = async (state: TaskState, reason?: string) => {
    if (state) {
      taskStateHandler(task.id, state, reason);
    }
  };

  return (
    <div data-label={ElementLocators.LOCATOR_MIB_CODEBACK_DETAIL_SECTION}>
      <FlexRow id={task.id.toString()} className={styles.Title}>
        <FlexCol className={styles.TitleTaskIcon}>
          <NoraTaskIcon task={task} onTaskStateClick={onTaskStateChange} />
        </FlexCol>
        <TitleSmall>{Resource.TASK_TITLE_MIB_CODEBACK}</TitleSmall>
      </FlexRow>
      {task.state === TaskState.WAIVED && task.waiveReason && (
        <FlexRow className={styles.TaskSubtitle}>
          <strong>{Resource.WAIVE_REASON}:</strong>&nbsp;{task.waiveReason}
        </FlexRow>
      )}
      <Spacer height={16} />
      <MIBCodebackDisplay task={task} />
    </div>
  );
};
